import { useFormBehaviors } from 'hooks';
import { createContext, FC, useContext, useMemo } from 'react';
import { UseFormBehaviors } from 'hooks/useFormBehaviors/useFormBehaviors.types';
import { useSeniorityRankingDataContext } from 'pages/SettingsPage/OrganizationSettingsPage/SeniorityRankingPage/SeniorityRankingPage.data.context';
import { useGQLMutation } from 'hooks/useGQLMutation.ts';
import {
  Mutation_UpdateOrganizationMutation,
  Mutation_UpdateOrganizationMutationVariables,
  SeniorityMethodEnum,
} from 'types/graphql';
import { MUTATION_updateOrganizationSeniorityMethod } from 'mutation';
import { toastService } from 'services';
import { handleGenericErrorMessage } from 'utils/handleGenericErrorMessage';
import { useTranslation } from 'hooks/useTranslation';

interface SeniorityRankingForm {
  seniority_method: string;
}

export const SeniorityRankingFormContext = createContext(
  {} as UseFormBehaviors<SeniorityRankingForm>,
);

export const SeniorityRankingFormProvider: FC = ({ children }) => {
  const { data, fetch } = useSeniorityRankingDataContext();
  const { t } = useTranslation();

  const { mutate, ...rest } = useGQLMutation<
    Mutation_UpdateOrganizationMutation,
    Mutation_UpdateOrganizationMutationVariables
  >({
    key: 'UPDATE_ORGANIZATION_SENIORITY_METHOD',
    mutation: MUTATION_updateOrganizationSeniorityMethod,
    refetchQueries: ['organizationSettingsPageSeniorityMethod'],
    onSuccess: () => {
      toastService.success(t('success:SENIORITY_METHOD'));
      fetch();
    },
    onError: handleGenericErrorMessage,
  });

  const form = useFormBehaviors({
    initialState: {
      seniority_method: `${data?.fetchCommunity?.organization?.seniorityMethod}`,
    },
    ...rest,
    onSubmit: (values) => {
      return mutate({
        variables: {
          organizationId: String(data?.fetchCommunity?.organization?.relayId),
          seniorityMethod: values.seniority_method as SeniorityMethodEnum,
        },
      });
    },
  });

  const value = useMemo(() => form, [form]);

  return (
    <SeniorityRankingFormContext.Provider value={value}>
      {children}
    </SeniorityRankingFormContext.Provider>
  );
};

export const useSeniorityRankingFormContext = () => useContext(SeniorityRankingFormContext);
